import React, { useState, useEffect } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ArrowLeftSquare } from "bootstrap-icons/icons/arrow-left-square-fill.svg";
import Spinner from "../../spinner/Spinner";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import formatDate from "../../../components/utils/DateFormatter";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";

const GetBillsDetails = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Get transaction data from state
    const transaction = location.state?.transaction;
    const type = location.state?.type;

    useEffect(() => {
        if (transaction) {
            setLoading(false);
        } else {
            toast.error("No transaction details found.");
            setLoading(false);
        }
    }, [transaction]);

    if (!transaction) {
        return (
            <MainLayouts>
                <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">No transaction data found</p>
                    <p className="text-sm text-gray-400 mb-4">
                        Please navigate from the bills list page to view transaction details.
                    </p>
                    <button
                        onClick={() => navigate("/admin/product/list")}
                        className="px-6 py-2 bg-orange1 text-white rounded-md hover:bg-orange-600"
                    >
                        Go Back to Bills List
                    </button>
                </div>
            </MainLayouts>
        );
    }

    const getStatusBadge = (status) => {
        let bgColor, text;

        switch (status) {
            case "1":
                bgColor = "bg-darkGreen3";
                text = "SUCCESSFUL";
                break;
            case "2":
                bgColor = "bg-dash3";
                text = "FAILED";
                break;
            case "0":
            case "3":
                bgColor = "bg-dash5";
                text = "PENDING";
                break;
            default:
                bgColor = "bg-gray-300";
                text = "UNKNOWN";
        }

        return (
            <span className={`${bgColor} text-dGrey3 text-xs font-medium px-3 py-1 rounded-full`}>
                {text}
            </span>
        );
    };

    const getTypeDisplay = () => {
        const typeMap = {
            'airtime': 'Airtime Purchase',
            'data': 'Data Subscription',
            'disco': 'Electricity Bill',
            'tv': 'TV Subscription'
        };
        return typeMap[type] || 'Transaction';
    };

    const formatResponse = (response) => {
        if (!response) return "N/A";

        try {
            if (typeof response === 'string') {
                const parsed = JSON.parse(response);
                return JSON.stringify(parsed, null, 2);
            }
            return JSON.stringify(response, null, 2);
        } catch (error) {
            return response;
        }
    };

    const getTransactionTitle = () => {
        switch (type) {
            case 'airtime':
                return `Airtime - ${transaction.network || ''}`;
            case 'data':
                return `Data - ${transaction.network || ''}`;
            case 'disco':
                return `Electricity - ${transaction.type || ''}`;
            case 'tv':
                return `TV - ${transaction.type || ''}`;
            default:
                return 'Transaction';
        }
    };

    return (
        <MainLayouts>
            <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                {/* Breadcrumb Section */}
                <section className="w-full pl-[3%] pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
                    <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                        <ol className="list-none p-0 inline-flex mb-2">
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                    Bills Product
                                </p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-orange1">Details</p>
                            </li>
                        </ol>
                    </nav>
                </section>

                {loading ? (
                    <Spinner loading={loading} />
                ) : (
                    <section className="text-gray-600 body-font">
                        <div className="container px-1 py-1 mx-auto">
                            {/* Header Card */}
                            <div className="flex flex-wrap -m-2">
                                <div className="p-2 w-full">
                                    <div className="h-full flex items-center p-4 rounded-lg bg-white shadow-sm">
                                        <div className="flex-grow">
                                            <h2 className="text-dash title-font font-medium uppercase">
                                                {getTransactionTitle()}
                                            </h2>
                                            
                                            <p className="text-gray-500 hidden md:block">
                                                <span className="text-gray-500">Amount: </span>
                                                <span className="text-dash">
                                                    {CurrencyFormatter(transaction.amount) || "0.00"}
                                                </span>
                                            </p>
                                            <p className="text-gray-500 hidden md:block">
                                                <span className="text-gray-500">Status: </span>
                                                <span className="text-dash">{getStatusBadge(transaction.status)}</span>
                                            </p>
                                        </div>
                                        <Link
                                            to="/admin/product/list"
                                            className="w-full sm:w-auto text-dGrey3 bg-darkGreen3 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                        >
                                            <span className="flex justify-center sm:justify-start mx-2">
                                                <ArrowLeftSquare className="mr-2" />
                                                Back
                                            </span>
                                        </Link>
                                    </div>
                                </div>

                                {/* Details Card */}
                                <div className="px-2 w-full">
                                    <div className="h-full flex items-center border-dash border p-4 rounded-lg border-l-4 bg-white shadow-sm">
                                        <div className="w-full pb-6">
                                            <div className="flex border-b border-gray-200 py-3">
                                                <span className="text-gray-500 w-1/3">Request ID</span>
                                                <span className="ml-auto text-dash font-semibold">
                                                    {transaction.request_id || "Not Available"}
                                                </span>
                                            </div>

                                            <div className="flex border-b border-gray-200 py-3">
                                                <span className="text-gray-500 w-1/3">Transaction Type</span>
                                                <span className="ml-auto text-dash font-semibold">
                                                    {getTypeDisplay()}
                                                </span>
                                            </div>

                                            {/* Amount Fields */}
                                            <div className="flex border-b border-gray-200 py-3">
                                                <span className="text-gray-500 w-1/3">Amount</span>
                                                <span className="ml-auto text-dash font-semibold">
                                                    {CurrencyFormatter(transaction.amount) || "0.00"}
                                                </span>
                                            </div>

                                            <div className="flex border-b border-gray-200 py-3">
                                                <span className="text-gray-500 w-1/3">Discounted Amount</span>
                                                <span className="ml-auto text-dash font-semibold">
                                                    {CurrencyFormatter(transaction.discounted_amount) || "0.00"}
                                                </span>
                                            </div>

                                            {/* Type-specific Information */}
                                            {type === 'airtime' && (
                                                <>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Phone Number</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.phone_number || "Not Available"}
                                                        </span>
                                                    </div>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Network</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.network || "Not Available"}
                                                        </span>
                                                    </div>
                                                </>
                                            )}

                                            {type === 'data' && (
                                                <>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Phone Number</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.phone_number || "Not Available"}
                                                        </span>
                                                    </div>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Network</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.network || "Not Available"}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Bundle</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.bundle || "Not Available"}
                                                        </span>
                                                    </div>
                                                </>
                                            )}

                                            {type === 'disco' && (
                                                <>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Customer Name</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.name || "Not Available"}
                                                        </span>
                                                    </div>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Meter Number</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.meterNo || "Not Available"}
                                                        </span>
                                                    </div>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Type</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.type || "Not Available"}
                                                        </span>
                                                    </div>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Address</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.address || "Not Available"}
                                                        </span>
                                                    </div>
                                                </>
                                            )}

                                            {type === 'tv' && (
                                                <>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Customer Name</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.customerName || "Not Available"}
                                                        </span>
                                                    </div>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Smart Card</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.smartcardNumber || "Not Available"}
                                                        </span>
                                                    </div>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Type</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.type || "Not Available"}
                                                        </span>
                                                    </div>
                                                    <div className="flex border-b border-gray-200 py-3">
                                                        <span className="text-gray-500 w-1/3">Period</span>
                                                        <span className="ml-auto text-dash font-semibold">
                                                            {transaction.period ? `${transaction.period} month(s)` : "Not Available"}
                                                        </span>
                                                    </div>
                                                </>
                                            )}

                                            {/* Common Fields */}
                                            <div className="flex border-b border-gray-200 py-3">
                                                <span className="text-gray-500 w-1/3">Date</span>
                                                <span className="ml-auto text-dash font-semibold">
                                                    {formatDate(transaction.created_at) || "Not Available"}
                                                </span>
                                            </div>

                                            {/* <div className="flex  py-3">
                                                <span className="text-gray-500 w-1/3">Payload</span>
                                                <span className="ml-auto text-dash font-semibold">
                                                        {transaction.payload || " "}
                                                </span>
                                            </div> */}
                                                <div className="flex  py-3">
                                                    <span className="text-gray-500 w-1/3">Payload</span>
                                                    <span className="ml-auto text-dash font-semibold">
                                                        <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">
                                                            {JSON.stringify(JSON.parse(transaction.payload || "{}"), null, 2)}
                                                        </pre>
                                                    </span>
                                                </div>

                                                
                                           

                                            {/* API Response Section */}
                                            {(transaction.response || transaction.payload) && (
                                                <div className="mt-4 pt-4 border-t border-gray-200">
                                                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Response</h3>
                                                    <div className="bg-gray-50 p-4 rounded-lg">
                                                        <pre className="text-xs overflow-x-auto whitespace-pre-wrap max-h-48">
                                                            {formatResponse(transaction.response || transaction.payload)}
                                                        </pre>
                                                    </div>
                                                </div>
                                            )}

                                            
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </MainLayouts>
    );
};

export default GetBillsDetails;